import {
  DataRoomActivityWindow,
  VisitorActivityPage,
  VisitorActivityRow,
  VisitorSortColumn,
} from 'shared';

import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { ApiService } from '../../../../services/index';
import { selectedCompanyStorage } from '../../../../storage/company/selected-company.util';
import { Order } from '../../../../types/general.types';
import { ACTIVITY_PAGE_SIZE, ActivityPages, useActivityPages } from './use-activity-pages.hook';

export type VisitorActivityFilters = {
  // One organisation, as the filter picks one. It goes as a plain value: the query builder writes a
  // list as JSON, which the server would read as a single organisation named '["Acme"]'.
  organisation?: string;
  search?: string;
  sortBy: VisitorSortColumn;
  sortOrder: Order;
  window: DataRoomActivityWindow;
};

const NO_ORGANISATIONS: string[] = [];

// A row is one visitor in one room, so neither half tells it apart on its own.
const visitorRoomKey = ({ dataRoomId, visitorKey }: VisitorActivityRow): string =>
  `${visitorKey}-${dataRoomId}`;

/**
 * The Visitors tab — 20.1.4 §1 to §4.
 *
 * A row is one visitor in one data room, and the organisation filter is sent as the name the
 * reader picked: the server translates it back into email domains, so the count and the page
 * stay in step with the filter instead of being narrowed after the fact.
 */
export const useActivityVisitors = (
  filters: VisitorActivityFilters,
): { organisations: string[] } & Omit<ActivityPages<VisitorActivityPage>, 'firstPage'> => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);

  const { firstPage, ...pages } = useActivityPages({
    fetchPage: (skip: number) =>
      ApiService.get<VisitorActivityPage>(
        { endpoint: BackendRoute.DATA_ROOM_ACTIVITY, routePath: ['visitors'] },
        {
          queryParams: {
            ...(filters.organisation ? { organisations: filters.organisation } : {}),
            ...(filters.search ? { search: filters.search } : {}),
            skip,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            take: ACTIVITY_PAGE_SIZE,
            window: filters.window,
          },
        },
      ),
    queryKey: [QueryKey.GET_DATA_ROOM_ACTIVITY_VISITORS, companyId, filters],
    rowKey: visitorRoomKey,
    scope: [QueryKey.GET_DATA_ROOM_ACTIVITY_VISITORS, companyId],
  });

  // Every page lists the same organisations — all of the window's — so the first one's serve.
  return { ...pages, organisations: firstPage?.organisations ?? NO_ORGANISATIONS };
};
